import styled from 'styled-components/macro';
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';

const Container = styled.div<{ height?: number }>`
  position: relative;
  display: inline-block;
  width: fit-content;
  height: auto;
  padding: 0;
  margin: 0;
  line-height: 0;
`;

export const TextArea = styled.textarea<{
  hasFeedBack?: boolean;
  feedbackColor?: string;
  minHeight?: number;
  maxHeight?: number;
  fontSize?: number;
  resize?: boolean;
  limitHeight?: boolean;
}>`
  font-family: ${({ theme }) => theme.fontFamily};
  box-sizing: border-box;
  min-width: 200px;
  width: 200px;
  height: auto;
  overflow-y: 'visible';
  min-height: ${({ minHeight }) => `${minHeight}px`};
  border: 1px solid
    ${({ hasFeedBack, feedbackColor, theme }) =>
      hasFeedBack
        ? feedbackColor || theme.colorPalette.red
        : theme.colorPalette.isDarkMode
        ? 'transparent'
        : theme.colorPalette.grey.grey5};
  border-radius: 3px;
  padding: 4px 10px;
  line-height: 1.4;
  margin: 0;
  resize: ${({ resize }) => (resize ? 'vertical' : 'none')};
  font-size: ${({ fontSize, theme }) => (fontSize ? `${fontSize}px` : theme.fontSizePalette.small)};
  background-color: ${({ theme }) =>
    theme.colorPalette.isDarkMode ? theme.colorPalette.grey.grey4 : theme.colorPalette.white};
  color: ${({ theme }) =>
    theme.colorPalette.isDarkMode ? theme.colorPalette.grey.grey10 : theme.colorPalette.grey.grey9};
  box-sizing: border-box;

  &::placeholder {
    color: ${({ theme }) => theme.colorPalette.grey.grey7};
  }

  &:focus {
    outline: 0;
    border: 1px solid
      ${({ hasFeedBack, feedbackColor, theme }) =>
        hasFeedBack ? feedbackColor || theme.colorPalette.red : theme.colorPalette.turquoise.normal};
  }

  ::-webkit-scrollbar {
    width: 3px;
  }
  ::-webkit-scrollbar-track {
    background: ${({ theme }) => theme.colorPalette.grey.grey5};
  }
  ::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.colorPalette.turquoise.normal};
  }
  ::-webkit-scrollbar-thumb:hover {
    background: ${({ theme }) => theme.colorPalette.turquoise.dark};
  }
  &:disabled {
    background-color: ${({ theme }) =>
      theme.colorPalette.isDarkMode ? theme.colorPalette.grey.grey1 : theme.colorPalette.grey.grey1};
    color: ${({ theme }) => (theme.colorPalette.isDarkMode ? theme.colorPalette.black : theme.colorPalette.grey.grey7)};
    opacity: 0.7
  }
`;

export const FeedbackText = styled.p<{ feedbackColor?: string }>`
  font-size: ${({ theme }) => theme.fontSizePalette.xxSmall};
  color: ${({ feedbackColor, theme }) => feedbackColor || theme.statusColors.error};
  text-align: right;
  position: absolute;
  right: 0;
  padding-top: 3px;
`;

export type Props = {
  onChange?: (event: React.ChangeEvent<HTMLTextAreaElement>) => void;
  onBlur?: (event: React.FocusEvent<HTMLTextAreaElement>) => void;
  onFocus?: (event: React.FocusEvent<HTMLTextAreaElement>) => void;
  hasFeedBack?: boolean;
  feedbackColor?: string;
  feedBackMessage?: string;
  value?: string;
  name?: string;
  fontSize?: number;
  placeholder?: string;
  maxHeight?: number;
  minHeight?: number;
  resize?: boolean;
  limitHeight?: boolean;
  disabled?: boolean;
};

function AutoResizeTextArea({
  hasFeedBack,
  feedBackMessage,
  feedbackColor,
  value = '',
  onChange,
  onBlur,
  onFocus,
  name,
  fontSize,
  placeholder,
  minHeight = 30,
  maxHeight = 60,
  resize,
  limitHeight = true,
  disabled,
}: Props) {
  const [currentValue, setCurrentValue] = useState(value);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setCurrentValue(e.target.value);
    onChange && onChange(e);
  };

  useLayoutEffect(() => {
    if (!textareaRef.current) return;

    const textarea = textareaRef.current;

    // reset the textarea height to minimum height to correctly calculate scrollHeight
    textarea.style.height = `${minHeight}px`;

    // get the scrollHeight which represents the full height of the content
    const newHeight = textarea.scrollHeight;

    // if limitHeight is true, clamp the height between minHeight and maxHeight
    // otherwise, use the newHeight without limitation
    const height = limitHeight ? `${Math.max(minHeight, Math.min(newHeight, maxHeight))}px` : `${newHeight}px`;

    // apply the calculated height to textarea and its container
    textarea.style.height = height;

    // if limiting height and content overflows maxHeight, enable vertical scrollbar
    // otherwise, hide the scrollbar
    textarea.style.overflowY = limitHeight && textareaRef.current.scrollHeight > maxHeight ? 'scroll' : 'hidden';
  }, [value, limitHeight, maxHeight, minHeight]);

  useEffect(() => {
    setCurrentValue(value);
  }, [value]);

  return (
    <Container ref={containerRef} height={minHeight}>
      <TextArea
        id={name}
        name={name}
        ref={textareaRef}
        value={currentValue}
        onChange={handleChange}
        onBlur={onBlur}
        onFocus={onFocus}
        hasFeedBack={hasFeedBack}
        feedbackColor={feedbackColor}
        minHeight={minHeight}
        maxHeight={maxHeight}
        fontSize={fontSize}
        placeholder={placeholder}
        resize={resize}
        limitHeight={limitHeight}
        disabled={disabled}
      />
      {hasFeedBack && feedBackMessage && <FeedbackText feedbackColor={feedbackColor}>{feedBackMessage}</FeedbackText>}
    </Container>
  );
}

export default AutoResizeTextArea;
